import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, ButtonGroup, Grid, Typography } from '@mui/material';
import { ArrowDropDown, ArrowLeft, Edit, LockOpen, Refresh } from '@mui/icons-material';
import { ChapterIndicatorPackageDto } from '../../../types/indicator';
import IndicatorPackageThemeItem from './IndicatorPackageThemeItem';
import FroalaIndicatorNote from '../editor/FroalaIndicatorNote';
import useStore from '../../../store/storeContext';
import { convertEpochToDateTimeWithoutSecondsString } from '../../../utils/dateUtils';

interface Props {
    indicatorPackage: ChapterIndicatorPackageDto;
    isApproved: boolean;
    isPreview: boolean;
    sharedData: string;
}

interface State {
    expanded: boolean;
}

function IndicatorPackageListItem(props: Props) {
    const { document, indicator, localization, loadingIndicator, snackbar, theming } = useStore();
    const { indicatorPackage, isPreview, isApproved, sharedData } = props;

    const [state, setState] = useState<State>({
        expanded: false,
    });

    useEffect(() => {
        setState((state) => ({ ...state, expanded: false }));
        return () => {
            if (indicatorPackage.locked) {
                // Unlock the locked package if the component gets unmounted.
                void document.unlockPackage(indicatorPackage.id!);
            }
        };
    }, []);

    useEffect(() => {
        if (!state.expanded && indicatorPackage.locked) {
            void document.unlockPackage(indicatorPackage.id!);
        }
    }, [state.expanded]);

    async function toggleEditing(): Promise<void> {
        indicatorPackage.locked
            ? await document.unlockPackage(props.indicatorPackage.id!)
            : await document.lockPackage(props.indicatorPackage.id!);
    }

    async function handleEditButtonClick(): Promise<void> {
        try {
            await toggleEditing();
        } catch (error) {
            if (error.data?.['UserMessage']) {
                snackbar.setMessage(error.data?.['UserMessage']);
                snackbar.setSeverity('error');
                snackbar.open();
            } else {
                snackbar.showError(error?.data?.['TranslationKey']);
            }
        }
    }

    async function togglePackage(): Promise<void> {
        if (props.indicatorPackage.themes.length > 0) {
            // We already got the themes. No need to fetch again.
            setState((state) => ({ ...state, expanded: !state.expanded }));
            return;
        }
        loadingIndicator.show();
        try {
            await indicator.fetchPackageThemes(props.indicatorPackage.id!);
            setState((state) => ({ ...state, expanded: !state.expanded }));
        } catch (error) {
            snackbar.showFetchFailedMessage(error.data?.code);
        } finally {
            loadingIndicator.hide();
        }
    }

    const { translate } = localization;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box display="flex">
                    <Box flexGrow={1}>
                        <Typography variant="h6">{indicatorPackage.name}</Typography>
                    </Box>
                    <ButtonGroup color="primary" size="small">
                        <Button onClick={togglePackage}>{state.expanded ? <ArrowDropDown /> : <ArrowLeft />}</Button>
                    </ButtonGroup>
                </Box>
            </Grid>
            {state.expanded && (
                <Grid item xs={12}>
                    {indicatorPackage.themes.map((theme) => (
                        <IndicatorPackageThemeItem
                            theme={theme}
                            key={theme.id}
                            editing={indicatorPackage.locked}
                            toggleEdit={toggleEditing}
                            sharedData={sharedData}
                            isPreview={isPreview}
                            isApproved={isApproved}
                        />
                    ))}
                    {
                        // In preview mode, show summary area only if shared data is either SHARE_SUMMARY or SHARE_INDICATORDESCRIPTION
                        (sharedData == 'DOCUMENT_APPROVAL_SHARE_SUMMARY' ||
                            sharedData == 'DOCUMENT_APPROVAL_SHARE_INDICATORDESCRIPTION' ||
                            !isPreview) && (
                            <Box style={{ padding: theming.spacing(2) }}>
                                <Box display="flex">
                                    <Box flexGrow={1}>
                                        <Typography variant="h6">
                                            {translate('SUMMARY')} - {indicatorPackage?.name}
                                        </Typography>
                                    </Box>
                                    <ButtonGroup id="button-group" color="primary" size="small">
                                        {!isApproved && !isPreview && (
                                            <Button
                                                onClick={handleEditButtonClick}
                                                style={{
                                                    color: !indicatorPackage.locked
                                                        ? theming.palette.primary.main
                                                        : theming.palette.error.main,
                                                }}
                                            >
                                                {!indicatorPackage.locked ? <Edit /> : <LockOpen />}
                                            </Button>
                                        )}
                                        <Button onClick={() => document.getIndicatorPackage(indicatorPackage)}>
                                            <Refresh />
                                        </Button>
                                    </ButtonGroup>
                                </Box>
                                <Typography variant="caption">
                                    <i>
                                        {`${translate('CHAPTER_LAST_UPDATED')} ${
                                            indicatorPackage ? indicatorPackage.editor?.userName || '' : ''
                                        } ${
                                            indicatorPackage
                                                ? convertEpochToDateTimeWithoutSecondsString(
                                                      indicatorPackage.lastUpdated,
                                                  )
                                                : ''
                                        }`}
                                    </i>
                                </Typography>
                                {indicatorPackage.locked ? (
                                    <FroalaIndicatorNote indicatorPackage={indicatorPackage} />
                                ) : (
                                    <Typography
                                        className="fr-view"
                                        dangerouslySetInnerHTML={{ __html: indicatorPackage?.notes || '' }}
                                    />
                                )}
                            </Box>
                        )
                    }
                </Grid>
            )}
        </Grid>
    );
}

export default observer(IndicatorPackageListItem);
